import React, { useState } from "react";

import { Grid, IconButton } from "@mui/material";
import {
  HighlightOffOutlined as HighlightOffOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  MailOutline as MailOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  PhoneIphone as PhoneIphoneIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";

import { useAppState } from "../../../context/StateContext";
import { Contact } from "../../../models/contact";
import { Timezone } from "../../../models/timezone";
import { CompanyProfileInitial } from "../../../util/enums";
import { formatTimezone } from "../../../util/Format";
import { isCompanyProfile } from "../../../util/validation";
import Button, { Color } from "../../Form/Button";
import ContactModal from "./ContactModal";
import useStyles from "./styles";

type Props = {
  contacts?: Contact[];
  viewOnly?: boolean;
  onChange?: (contacts: Contact[]) => void;
  timezones?: Timezone[];
};

const ListContacts = (props: Props) => {
  const { contacts, viewOnly, onChange, timezones } = props;
  const { currentUser } = useAppState();

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const [contactSelected, setContactSelected] = useState<Contact>();
  const [indexContactSelected, setIndexContactSelected] = useState<number>();

  const [contactModalIsOpen, setContactModalIsOpen] = useState<boolean>(false);

  const handleChange = (index: number, contact: Contact) => {
    const contactsUpdated = [...(contacts || [])];

    if (contactsUpdated && contactsUpdated?.length > 0 && contactsUpdated[Number(index)]) {
      contactsUpdated[Number(index)] = {
        ...contactsUpdated[Number(index)],
        ...contact,
      };
      if (onChange) onChange(contactsUpdated);
    }
  };

  const handleClose = (contact?: Contact) => {
    if (contact) {
      if (!contacts || contacts?.length === 0) {
        contact = { ...contact, isPrimary: true };
      }
      if (indexContactSelected !== undefined) {
        handleChange(indexContactSelected, contact);
      } else {
        const contactsUpdated = [...(contacts || [])];

        contactsUpdated.push(contact);
        if (onChange) onChange(contactsUpdated);
      }
    }

    setContactModalIsOpen(false);
    setContactSelected(undefined);
    setIndexContactSelected(undefined);
  };

  const onEdit = (contact: Contact, index: number) => {
    let { mobilePhone } = contact;

    if (!isCompanyProfile(currentUser?.company, CompanyProfileInitial?.MV)) {
      if (mobilePhone && !mobilePhone.includes("+")) mobilePhone = `+${contact?.mobilePhone}`;
    }

    setContactSelected({ ...contact, mobilePhone });
    setIndexContactSelected(index);
    setContactModalIsOpen(true);
  };

  const onDelete = (contact: Contact, index: number) => {
    if (contact?.id) {
      handleChange(index, { ...contact, valid: false });
    } else {
      const contactsUpdated = [...(contacts || [])];

      delete contactsUpdated[index];
      if (onChange) onChange(contactsUpdated.filter(Boolean));
    }
  };

  const renderDeleteButton = (contact: Contact, index: number) => {
    if (!contact?.isPrimary) {
      return (
        <IconButton
          disabled={viewOnly}
          className={classes.iconButton}
          onClick={() => onDelete(contact, index)}
        >
          <HighlightOffOutlinedIcon className={classes.icon} htmlColor="#F97066" />
        </IconButton>
      );
    }

    return null;
  };

  const renderContacts = () => {
    if (contacts && contacts.length > 0) {
      return (
        <>
          {contacts
            ?.filter((contact) => contact.valid)
            ?.sort((contactA, contactB) => {
              if (contactA?.isPrimary === contactB?.isPrimary) return 0;
              if (contactA?.isPrimary) return -1;

              return 1;
            })
            ?.map((contact, index) => {
              const { timezoneId } = contact;
              const timezone = timezones?.find((timezone) => timezone.id === timezoneId);

              return (
                <Grid key={index} container item direction="row" spacing={2}>
                  <Grid item alignItems="center">
                    <div className={classes.itemCenter}>
                      <PersonOutlineOutlinedIcon className={classes.icon} htmlColor="#98A2B3" />
                      <span className={classes.info}>
                        {contact?.firstName} {contact?.lastName}
                      </span>
                    </div>
                  </Grid>
                  <Grid item alignItems="center">
                    <div className={classes.itemCenter}>
                      <MailOutlineIcon className={classes.icon} htmlColor="#98A2B3" />
                      <span className={classes.info}>{contact?.email}</span>
                    </div>
                  </Grid>
                  <Grid item alignItems="center">
                    <div className={classes.itemCenter}>
                      <PhoneIphoneIcon className={classes.icon} htmlColor="#98A2B3" />
                      <span className={classes.info}>{contact?.mobilePhone}</span>
                    </div>
                  </Grid>
                  {contact?.phone && (
                    <Grid item alignItems="center">
                      <div className={classes.itemCenter}>
                        <PhoneOutlinedIcon className={classes.icon} htmlColor="#98A2B3" />
                        <span className={classes.info}>{contact?.phone}</span>
                      </div>
                    </Grid>
                  )}
                  {timezone && (
                    <Grid item alignItems="center">
                      <div className={classes.itemCenter}>
                        <PublicIcon className={classes.icon} htmlColor="#98A2B3" />
                        <span className={classes.info}>{formatTimezone(timezone)}</span>
                      </div>
                    </Grid>
                  )}
                  <Grid item>
                    <IconButton
                      disabled={viewOnly}
                      className={classes.iconButton}
                      onClick={() => onEdit(contact, index)}
                    >
                      <FiEdit className={classes.icon} color="#98A2B3" />
                    </IconButton>
                    {renderDeleteButton(contact, index)}
                  </Grid>
                </Grid>
              );
            })}
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <ContactModal
        open={contactModalIsOpen}
        contact={contactSelected}
        handleClose={handleClose}
        timezones={timezones}
      />
      <Grid container direction="row" spacing={1}>
        {renderContacts()}
        <Grid item md={12}>
          <Button
            data-cy="add-contact-button"
            disabled={viewOnly}
            customColor={Color.SECONDARY}
            onClick={() => setContactModalIsOpen(true)}
            startIcon={<AddCircleOutlineIcon htmlColor="#FFF" />}
          >
            {translate("addNewContact")}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default ListContacts;
